// Stage 4 of the Smart Asset Import pipeline: resolve cell values against AssetPro
// master data by EXACT match only.
//
// Purpose in v1 (CSV output): the import template carries NAMES + generated codes, not
// AssetPro _ids, so resolution here is about VALIDATION, not ID substitution. For each
// business-critical value we determine whether it already EXISTS in AssetPro's masters
// (asset types, sub-types, models, makes, locations, owners). Existing values can flow
// through; values with no exact master match are flagged "needs new master record" and
// routed to review — never fuzzy-matched onto a near neighbour.
//
// Matching uses normalizeKey, which folds only case/whitespace/separators. Spelling
// variants (KIRLOSKER vs KIRLOSKAR) intentionally resolve to UNMATCHED.

import { apiGet } from './apiService';
import { decodeEntityNames } from './htmlDecode';
import { normalizeKey } from './textNormalize';

// Extract an array from the various response envelope shapes the API uses.
// Mirrors the `extract` helper in AssetReclassification.js.
const extract = (res) => {
  if (!res || !res.success) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.subTypes)) return d.subTypes;
  if (d && Array.isArray(d.models)) return d.models;
  if (d && Array.isArray(d.assets)) return d.assets;
  return [];
};

const toNameSet = (arr) => {
  const set = new Set();
  (arr || []).forEach((e) => {
    if (e && typeof e === 'object' && typeof e.name === 'string') {
      const key = normalizeKey(e.name);
      if (key) set.add(key);
    }
  });
  return set;
};

/**
 * Fetch AssetPro master data and build normalized name-sets per kind.
 * Reuses the granular endpoints already proven in AssetReclassification.js.
 *
 * @returns {Promise<{
 *   available:boolean,
 *   sets:{ assetType:Set, assetSubType:Set, model:Set, make:Set, location:Set, owner:Set },
 *   counts:Object<string,number>,
 *   error?:string
 * }>}
 */
export const fetchMasterData = async () => {
  const opts = { headers: { Accept: '*/*' } };
  try {
    const [typeRes, subRes, modelRes, makeRes, locRes, ownerRes] = await Promise.all([
      apiGet('/asset-type', opts),
      apiGet('/equipment-sub-type', opts),
      apiGet('/asset-model', opts),
      apiGet('/asset-make', opts),
      apiGet('/location', opts),
      apiGet('/owner', opts).catch(() => ({ success: false })),
    ]);

    const sets = {
      assetType: toNameSet(decodeEntityNames(extract(typeRes))),
      assetSubType: toNameSet(decodeEntityNames(extract(subRes))),
      model: toNameSet(decodeEntityNames(extract(modelRes))),
      make: toNameSet(decodeEntityNames(extract(makeRes))),
      location: toNameSet(decodeEntityNames(extract(locRes))),
      owner: toNameSet(decodeEntityNames(extract(ownerRes))),
    };
    const counts = Object.fromEntries(Object.keys(sets).map((k) => [k, sets[k].size]));
    // Consider master data "available" only if the core taxonomy actually returned rows;
    // otherwise resolution would mislabel everything as new.
    const available = sets.assetType.size > 0 || sets.assetSubType.size > 0 || sets.model.size > 0;
    return { available, sets, counts };
  } catch (error) {
    return {
      available: false,
      sets: { assetType: new Set(), assetSubType: new Set(), model: new Set(), make: new Set(), location: new Set(), owner: new Set() },
      counts: {},
      error: error.message,
    };
  }
};

/** Build an empty masters object (used in tests / when master data is unavailable). */
export const emptyMasters = () => ({
  available: false,
  sets: { assetType: new Set(), assetSubType: new Set(), model: new Set(), make: new Set(), location: new Set(), owner: new Set() },
  counts: {},
});

/**
 * Resolve one value against a master kind.
 * @returns {{ status:'EXACT'|'UNMATCHED'|'EMPTY'|'NO_MASTER', kind:string }}
 */
export const resolveValue = (kind, value, masters) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return { status: 'EMPTY', kind };
  }
  if (!masters || !masters.available) {
    // No master data to check against — cannot claim EXACT. Caller decides handling.
    return { status: 'NO_MASTER', kind };
  }
  const set = masters.sets[kind];
  if (!set) return { status: 'NO_MASTER', kind };
  return { status: set.has(normalizeKey(value)) ? 'EXACT' : 'UNMATCHED', kind };
};
